<template>
  <canvas ref="labCanvas" id="lab-canvas"></canvas>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const labCanvas = ref(null);
let animationFrameId = null;
let w, h;
let particles = [];
// 鼠标交互对象
const mouse = { x: null, y: null, radius: 200 };

// 颜色配置
const hexColor = 'rgba(0, 102, 204, 0.15)';
const bubbleColor = 'rgba(0, 210, 255, 0.2)';

class Particle {
  constructor(type) {
    this.type = type;
    this.x = Math.random() * w;
    this.y = Math.random() * h;
    this.size = Math.random() * 5 + 2;
    
    // 六边形粒子
    if (type === 'hex') {
      this.vx = (Math.random() - 0.5) * 0.5;
      this.vy = (Math.random() - 0.5) * 0.5;
      this.angle = Math.random() * Math.PI * 2;
      this.spin = (Math.random() - 0.5) * 0.02;
    } 
    // 气泡粒子
    else {
      this.vx = (Math.random() - 0.5) * 0.2;
      this.vy = -Math.random() * 1 - 0.2; // 向上浮动
    }
  }

  draw(ctx) {
    ctx.beginPath();
    if (this.type === 'hex') {
      for (let i = 0; i < 6; i++) {
        ctx.lineTo(
          this.x + this.size * 3 * Math.cos(this.angle + i * Math.PI / 3),
          this.y + this.size * 3 * Math.sin(this.angle + i * Math.PI / 3)
        );
      }
      ctx.closePath();
      ctx.strokeStyle = hexColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    } else {
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = bubbleColor;
      ctx.fill();
    }
  }

  update(ctx) {
    this.x += this.vx;
    this.y += this.vy;
    
    if (this.type === 'hex') this.angle += this.spin;
    
    // 边界检查与重置
    if (this.y < -50) this.y = h + 50;
    if (this.y > h + 50) this.y = -50;
    if (this.x < -50) this.x = w + 50;
    if (this.x > w + 50) this.x = -50;

    // 鼠标互动逻辑
    if (mouse.x != null) {
      let dx = mouse.x - this.x;
      let dy = mouse.y - this.y;
      let distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < mouse.radius) {
        const force = (mouse.radius - distance) / mouse.radius;
        this.x += (dx / distance) * force * 1.5;
        this.y += (dy / distance) * force * 1.5;
        
        // 连线效果
        ctx.beginPath();
        ctx.strokeStyle = `rgba(0, 210, 255, ${1 - distance / mouse.radius})`;
        ctx.lineWidth = 1;
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();
      }
    }
  }
}

const initCanvasAnimation = () => {
  const canvas = labCanvas.value;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  const resize = () => {
    w = canvas.width = window.innerWidth;
    h = canvas.height = window.innerHeight;
  };

  const initParticles = () => {
    particles = [];
    const isMobile = window.innerWidth < 768;
    const hexCount = isMobile ? 15 : 30;
    const bubbleCount = isMobile ? 25 : 50;
    for (let i = 0; i < hexCount; i++) particles.push(new Particle('hex'));
    for (let i = 0; i < bubbleCount; i++) particles.push(new Particle('bubble'));
  };

  const animate = () => {
    ctx.clearRect(0, 0, w, h);
    particles.forEach(p => { p.draw(ctx); p.update(ctx); });
    animationFrameId = requestAnimationFrame(animate);
  };

  // 事件监听
  window.addEventListener('resize', resize);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseout', handleMouseOut);

  // 初始化执行
  resize();
  initParticles();
  animate();
};

const handleMouseMove = (e) => {
  mouse.x = e.x; 
  mouse.y = e.y;
};

const handleMouseOut = () => {
  mouse.x = null; 
  mouse.y = null;
};

onMounted(() => {
  initCanvasAnimation();
});

onUnmounted(() => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
  window.removeEventListener('resize', () => {}); // 注意：这里简化了移除，实际应提取 resize 函数引用
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseout', handleMouseOut);
});
</script>

<style scoped>
#lab-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none; /* 关键：让鼠标事件穿透 Canvas，不影响页面点击 */
}
</style>